import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Paper,
  Group,
  Stack,
  Text,
  Button,
  Loader,
  Badge,
  TextInput,
  Table,
  Checkbox,
  Select,
  Modal,
  Pagination,
  Tooltip,
  Divider,
  Tabs,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconCheck,
  IconRefresh,
  IconFilter,
  IconEye,
  IconFlask,
  IconUrgent,
  IconAlertTriangle,
  IconTruckDelivery,
  IconScan,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { SlideInsufficientTissueModal } from "../../components/workflows/SlideInsufficientTissueModal";
import { SlideInsufficientTissueCheckbox } from "../../components/workflows/SlideInsufficientTissueCheckbox";
import {
  useDistributionQueue,
  useProcessDistributionScan,
  useConfirmDistribution,
  useDistributionPathologists,
  useAssignPathologistAtDistribution,
  useDistributionCaseSlides,
  useMarkSlideReadyForReview,
  useMarkSlideNotReadyForReview,
  useConfirmScan,
  useDistributionQueueCounts,
  WORKFLOW_EVENT_CHANNEL,
} from "../../hooks/useWorkflows";
import type {
  DistributionScanResult,
  DistributionCase,
  DistributionSlideInfo,
  DistributionQueueView,
} from "../../services/workflows.service";
import {
  CaseStatusBadge,
  FilterModal,
  ActiveFilters,
} from "../../components/cases";
import { type CaseFilters } from "../../services";
import {
  HeControlBanner,
  MissingControlsBanner,
  ControlConfirmationModal,
} from "../../components/workflows";
import {
  isCompleteDistributionSlideBarcode,
  useBarcodeScanner,
} from "../../hooks/useBarcodeScanner";
import { getDisplayCaseNumber } from "../../utils/caseIdentity";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";

const ITEMS_PER_PAGE = 20;

function formatQueueDate(value: unknown): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "—";
}

export default function DistributionPage() {
  const navigate = useNavigate();
  const [tissueCaseId, setTissueCaseId] = useState<string | null>(null);
  const handleOpenTissue = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setTissueCaseId(event.currentTarget.dataset.caseId || null);
    },
    []
  );
  const handleCloseTissue = useCallback(() => setTissueCaseId(null), []);
  const [activeTab, setActiveTab] = useState<string | null>("distribution");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [selectedCases, setSelectedCases] = useState<Set<string>>(new Set());
  const [scannedCase, setScannedCase] = useState<DistributionScanResult | null>(
    null
  );
  const [viewingCaseId, setViewingCaseId] = useState<string | null>(null);
  const [filters, setFilters] = useState<CaseFilters>({});
  const [page, setPage] = useState(1);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    controlModalOpened,
    { open: openControlModal, close: closeControlModal },
  ] = useDisclosure(false);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const queueView: DistributionQueueView =
    activeTab === "scanner" ? "scanner" : "distribution";
  const queueRequest = useMemo(
    () => ({ view: queueView, page, limit: ITEMS_PER_PAGE, filters }),
    [filters, page, queueView]
  );
  const {
    data: queueResponse,
    isLoading,
    refetch,
  } = useDistributionQueue(queueRequest);
  const pendingCases = queueResponse?.data ?? [];
  const pagination = queueResponse?.pagination;
  const totalCases = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 0;
  const { data: queueCounts } = useDistributionQueueCounts();
  const { data: pathologists } = useDistributionPathologists();
  const scanMutation = useProcessDistributionScan();
  const confirmMutation = useConfirmDistribution();
  const confirmScanMutation = useConfirmScan();
  const assignPathologistMutation = useAssignPathologistAtDistribution();
  const { data: viewingCaseSlides, refetch: refetchCaseSlides } =
    useDistributionCaseSlides(viewingCaseId);
  const markSlideReadyMutation = useMarkSlideReadyForReview();
  const markSlideNotReadyMutation = useMarkSlideNotReadyForReview();

  // Add cross-tab synchronization listener
  useEffect(() => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window))
      return;

    const channel = new BroadcastChannel(WORKFLOW_EVENT_CHANNEL);
    channel.onmessage = (event) => {
      if (
        event.data.type === "accessioning-updated" ||
        event.data.type === "accessioning-completed" ||
        event.data.type === "case-scanned"
      ) {
        // Refetch the pending list
        refetch();

        // If the updated case is the one we are viewing, refetch slides too
        if (viewingCaseId && viewingCaseId === event.data.caseId) {
          refetchCaseSlides();
        }
      }
    };

    return () => {
      channel.close();
    };
  }, [refetch, refetchCaseSlides, viewingCaseId]);

  // Check if any filters are active (excluding search)
  const hasActiveFilters = Object.values(filters).some((value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    if (typeof value === "boolean") return value;
    return !!value;
  });

  const handleFiltersApply = (newFilters: CaseFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleActiveFiltersChange = (newFilters: CaseFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleTabChange = useCallback((value: string | null) => {
    setActiveTab(value);
    setPage(1);
  }, []);

  const processBarcode = useCallback(
    async (barcode: string) => {
      try {
        const result = await scanMutation.mutateAsync({
          barcode,
          expectedCaseId: viewingCaseId ?? undefined,
        });
        setBarcodeInput("");
        setScannedCase(result);
        setViewingCaseId(result.case.id);

        const scanTypeLabels = {
          slide: "Slide",
          block: "Block/Cassette",
          case: "Case",
        };
        const scanLabel = scanTypeLabels[result.scanType];

        if (result.alreadyReady) {
          notifications.show({
            title: "Already Scanned",
            message: `${scanLabel} was already marked as ready for review`,
            color: "yellow",
          });
        } else {
          const slidesText =
            result.slidesMarkedReady === 1
              ? "1 slide"
              : `${result.slidesMarkedReady} slides`;
          const message = result.stainingCompleted
            ? `Staining completed and ${slidesText} marked as ready for review`
            : `${slidesText} marked as ready for review`;
          notifications.show({
            title: `${scanLabel} Scanned`,
            message,
            color: "green",
          });
        }

        if (result.isCaseReady) {
          notifications.show({
            title: "Case Ready",
            message: `All slides for case ${getDisplayCaseNumber(
              result.case
            )} are ready for distribution`,
            color: "blue",
          });
        }

        refetch();
      } catch (error: any) {
        const isOwnerMismatch = error.code === "DISTRIBUTION_CASE_MISMATCH";
        notifications.show({
          title: isOwnerMismatch ? "Different Case" : "Scan Error",
          message: isOwnerMismatch
            ? "Finish or close the open case before scanning a different case."
            : error.message || "Failed to process scan",
          color: isOwnerMismatch ? "yellow" : "red",
        });
        setBarcodeInput("");
      }
    },
    [refetch, scanMutation, viewingCaseId]
  );

  const handleBarcodeSubmit = () => {
    const barcode = barcodeInput.trim();
    if (barcode) void processBarcode(barcode);
  };

  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBarcodeSubmit();
    }
  };

  useBarcodeScanner(processBarcode, {
    enabled: true,
    ownerKey: `distribution:${activeTab}:${viewingCaseId ?? "queue"}`,
    isCompleteBarcode: isCompleteDistributionSlideBarcode,
  });

  const handleAssignPathologist = useCallback(
    async (caseId: string, pathologistId: string) => {
      try {
        await assignPathologistMutation.mutateAsync({ caseId, pathologistId });
        notifications.show({
          title: "Success",
          message: "Pathologist assigned",
          color: "green",
        });

        // Update scanned case if it's the same case
        if (scannedCase && scannedCase.case.id === caseId) {
          setScannedCase({
            ...scannedCase,
            case: {
              ...scannedCase.case,
              pathologistId,
            },
          });
        }

        if (viewingCaseId) {
          refetchCaseSlides();
        }
      } catch {
        notifications.show({
          title: "Error",
          message: "Unable to assign the pathologist. Please try again.",
          color: "red",
        });
      }
    },
    [assignPathologistMutation, refetchCaseSlides, scannedCase, viewingCaseId]
  );

  const handleConfirmDistribution = async () => {
    if (selectedCases.size === 0) {
      notifications.show({
        title: "No Selection",
        message: "Please select at least one case",
        color: "blue",
      });
      return;
    }

    try {
      const result = await confirmMutation.mutateAsync(
        Array.from(selectedCases)
      );

      // Show routing information
      if (result.routed && result.routed.length > 0) {
        const routedToReview = result.routed.filter(
          (r) => r.destination === "Pending Pathologist Review"
        ).length;
        const routedToShipping = result.routed.filter(
          (r) => r.destination === "Pending Shipping"
        ).length;
        const routedToExternalReport = result.routed.filter(
          (r) => r.destination === "Pending External Report"
        ).length;

        let message = `Confirmed distribution for ${result.routed.length} case(s)`;
        if (routedToReview > 0)
          message += ` - ${routedToReview} to Pathologist Review`;
        if (routedToShipping > 0)
          message += ` - ${routedToShipping} to Shipping`;
        if (routedToExternalReport > 0)
          message += ` - ${routedToExternalReport} awaiting External Report`;

        notifications.show({
          title: "Success",
          message,
          color: "green",
        });
      } else {
        notifications.show({
          title: "Success",
          message: `Confirmed distribution for ${selectedCases.size} case(s)`,
          color: "green",
        });
      }

      setSelectedCases(new Set());
      setScannedCase(null);
      refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to confirm distribution",
        color: "red",
      });
    }
  };

  const handleConfirmSingle = async (caseId: string) => {
    try {
      const result = await confirmMutation.mutateAsync([caseId]);

      const routeInfo = result.routed?.[0];
      notifications.show({
        title: "Success",
        message: routeInfo
          ? `Case routed to ${routeInfo.destination}`
          : "Confirmed distribution for case",
        color: "green",
      });

      const newSet = new Set(selectedCases);
      newSet.delete(caseId);
      setSelectedCases(newSet);

      // Clear modal if it was this case
      if (scannedCase?.case.id === caseId) {
        setScannedCase(null);
      }
      if (viewingCaseId === caseId) {
        setViewingCaseId(null);
      }

      refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to confirm distribution",
        color: "red",
      });
    }
  };

  const handleConfirmScan = async (caseId: string) => {
    try {
      await confirmScanMutation.mutateAsync(caseId);
      notifications.show({
        title: "Scan Confirmed",
        message: "Case marked as scanned and routed to Pathologist Review",
        color: "green",
      });
      refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to confirm scan",
        color: "red",
      });
    }
  };

  // Build pathologist dropdown options (sorted alphabetically)
  const pathologistOptions = useMemo(
    () =>
      (pathologists || [])
        .map((p) => ({
          value: p.id,
          label: `${p.fullName} (${p.isRemote ? "Remote" : "Local"})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [pathologists]
  );

  // Unified modal data: from scan result or manual view
  const modalData = useMemo(() => {
    if (scannedCase) {
      return {
        caseInfo: scannedCase.case,
        slides: scannedCase.allCaseSlides,
        slidesReady: scannedCase.slidesReady,
        slidesTotal: scannedCase.slidesTotal,
        isCaseReady: scannedCase.isCaseReady,
        source: "scan" as const,
      };
    }
    if (viewingCaseSlides) {
      return {
        caseInfo: viewingCaseSlides.case,
        slides: viewingCaseSlides.allCaseSlides,
        slidesReady: viewingCaseSlides.slidesReady,
        slidesTotal: viewingCaseSlides.slidesTotal,
        isCaseReady: viewingCaseSlides.isCaseReady,
        source: "manual" as const,
      };
    }
    return null;
  }, [scannedCase, viewingCaseSlides]);

  const isModalOpen = !!scannedCase || !!viewingCaseId;

  const handlePathologistSelection = useCallback(
    (pathologistId: string | null) => {
      if (!pathologistId || !modalData) {
        return;
      }
      if (assignPathologistMutation.isPending) {
        return;
      }
      void handleAssignPathologist(modalData.caseInfo.id, pathologistId);
    },
    [assignPathologistMutation.isPending, handleAssignPathologist, modalData]
  );

  const closeModal = useCallback(() => {
    setScannedCase(null);
    setViewingCaseId(null);
  }, []);

  const handleMarkSlideReady = useCallback(
    async (slideId: string) => {
      try {
        const result = await markSlideReadyMutation.mutateAsync(slideId);
        // If opened from scan, update local scanned case state
        if (scannedCase) {
          const updated = { ...scannedCase };
          updated.allCaseSlides = updated.allCaseSlides.map((s) =>
            s.id === slideId
              ? {
                  ...s,
                  readyForReview: true,
                  readyForReviewAt: new Date().toISOString(),
                  ...(result.stainingCompleted && {
                    status: "staining_completed",
                  }),
                }
              : s
          );
          updated.slidesReady = updated.allCaseSlides.filter(
            (s) => s.requiresConfirmation !== false && s.readyForReview
          ).length;
          updated.isCaseReady =
            updated.slidesReady === updated.slidesTotal &&
            updated.slidesTotal > 0;
          setScannedCase(updated);
        }
        // If opened from manual view, refetch will update automatically
        if (viewingCaseId) {
          refetchCaseSlides();
        }
        refetch();
        notifications.show({
          title: "Slide Marked Ready",
          message: result.stainingCompleted
            ? "Staining completed and slide marked as ready for review"
            : "Slide marked as ready for review",
          color: "green",
        });
      } catch (error: any) {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to mark slide as ready",
          color: "red",
        });
      }
    },
    [
      scannedCase,
      viewingCaseId,
      markSlideReadyMutation,
      refetchCaseSlides,
      refetch,
    ]
  );

  const handleMarkSlideNotReady = useCallback(
    async (slideId: string) => {
      try {
        const result = await markSlideNotReadyMutation.mutateAsync(slideId);
        if (scannedCase) {
          const updated = { ...scannedCase };
          updated.allCaseSlides = updated.allCaseSlides.map((s) =>
            s.id === slideId
              ? { ...s, readyForReview: false, readyForReviewAt: null }
              : s
          );
          updated.slidesReady = result.slidesReady;
          updated.isCaseReady = result.isCaseReady;
          setScannedCase(updated);
        }
        if (viewingCaseId) {
          await refetchCaseSlides();
        }
        await refetch();
        notifications.show({
          title: "Slide Unmarked",
          message: "Slide marked as not ready for review",
          color: "yellow",
        });
      } catch (error: any) {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to unmark slide",
          color: "red",
        });
      }
    },
    [
      scannedCase,
      viewingCaseId,
      markSlideNotReadyMutation,
      refetchCaseSlides,
      refetch,
    ]
  );

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between">
            <Title order={2}>Distribution</Title>
            <Group gap="sm">
              <Button
                variant="light"
                leftSection={<IconFlask size={16} />}
                onClick={() => navigate("/distribution/control-slides")}
              >
                Control Slides
              </Button>
              {selectedCases.size > 0 && (
                <Button
                  leftSection={<IconCheck size={16} />}
                  onClick={handleConfirmDistribution}
                  loading={confirmMutation.isPending}
                >
                  Confirm Distribution ({selectedCases.size})
                </Button>
              )}
            </Group>
          </Group>

          <HeControlBanner onConfirmClick={openControlModal} />
          <MissingControlsBanner />

          <Tabs value={activeTab} onChange={handleTabChange} variant="outline">
            <Tabs.List>
              <Tabs.Tab
                value="distribution"
                leftSection={<IconTruckDelivery size={16} />}
              >
                Distribution Queue ({queueCounts?.pendingDistribution ?? 0})
              </Tabs.Tab>
              <Tabs.Tab value="scanner" leftSection={<IconScan size={16} />}>
                Scanner Handoff ({queueCounts?.awaitingScan ?? 0})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="distribution" pt="md">
              {/* Barcode Scanner and Filters Row */}
              <Group align="stretch" grow>
                {/* Barcode Scanner Input */}
                <Paper p="md" withBorder style={{ flex: 2 }}>
                  <Stack gap="sm">
                    <Group>
                      <IconBarcode size={24} />
                      <Text fw={500}>Scan Slide Barcode</Text>
                    </Group>
                    <Group>
                      <TextInput
                        ref={barcodeInputRef}
                        placeholder="Scan barcode (case, cassette, or slide)"
                        value={barcodeInput}
                        onChange={(e) => setBarcodeInput(e.target.value)}
                        onKeyPress={handleBarcodeKeyPress}
                        style={{ flex: 1 }}
                        autoFocus
                      />
                      <Button
                        onClick={handleBarcodeSubmit}
                        loading={scanMutation.isPending}
                        disabled={!barcodeInput.trim()}
                      >
                        Scan
                      </Button>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Scan a slide, cassette, or case barcode to mark slides as
                      ready for review
                    </Text>
                  </Stack>
                </Paper>

                {/* Queue Filters */}
                <Paper p="md" withBorder style={{ flex: 1 }}>
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Group>
                        <IconFilter size={24} />
                        <Text fw={500}>Queue Filters</Text>
                      </Group>
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        onClick={() => refetch()}
                        loading={isLoading}
                        leftSection={<IconRefresh size={16} />}
                      />
                    </Group>
                    <Button variant="light" onClick={openFilterModal} fullWidth>
                      {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                    </Button>
                    {hasActiveFilters && (
                      <ActiveFilters
                        filters={filters}
                        onFiltersChange={handleActiveFiltersChange}
                        onClearAll={handleClearAllFilters}
                      />
                    )}
                    <Text size="sm" c="dimmed">
                      Showing {totalCases} cases
                    </Text>
                  </Stack>
                </Paper>
              </Group>
            </Tabs.Panel>

            <Tabs.Panel value="scanner" pt="md">
              <Paper p="md" withBorder>
                <Group justify="space-between">
                  <Stack gap={4}>
                    <Group>
                      <IconScan size={24} />
                      <Text fw={500}>Digital Scanner Handoff</Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Confirm cases that have been loaded into the digital
                      scanner. This will make them visible to pathologists.
                    </Text>
                  </Stack>
                  <Button
                    variant="subtle"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  >
                    Refresh
                  </Button>
                </Group>
              </Paper>
            </Tabs.Panel>
          </Tabs>

          {/* Cases Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  {activeTab === "scanner"
                    ? "Scanner Handoff Queue"
                    : "Distribution Queue"}{" "}
                  ({totalCases})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, totalCases)} of{" "}
                      {totalCases} cases
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Group justify="center" p="xl">
                  <Loader />
                </Group>
              ) : pendingCases.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters
                    ? "No cases match the current filters"
                    : "No cases in staining or distribution queue"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Select</Table.Th>
                      <Table.Th style={{ whiteSpace: "nowrap", minWidth: 140 }}>
                        Case
                      </Table.Th>
                      <Table.Th>Patient</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Workflow</Table.Th>
                      <Table.Th>Pathologist</Table.Th>
                      <Table.Th>Practice / Center</Table.Th>
                      <Table.Th>Physician</Table.Th>
                      <Table.Th>Slides Ready</Table.Th>
                      <Table.Th>Actions</Table.Th>
                      {queueView === "scanner" && (
                        <Table.Th>Insufficient tissue</Table.Th>
                      )}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {pendingCases.map((caseData: DistributionCase) => {
                      const slides = caseData.slides || [];
                      const confirmableSlides = slides.filter(
                        (slide) =>
                          slide.requiresConfirmation !== false &&
                          slide.stain?.processingKind !== "unstained"
                      ).length;
                      const readyCount = slides.filter(
                        (slide) =>
                          slide.requiresConfirmation !== false &&
                          slide.stain?.processingKind !== "unstained" &&
                          slide.readyForReviewAt
                      ).length;
                      const totalCount = confirmableSlides;
                      const isSelected = selectedCases.has(caseData.id);
                      const allSlidesReady =
                        readyCount === totalCount && totalCount > 0;
                      const isPendingDistribution =
                        caseData.currentStatus?.name === "Pending Distribution";
                      const statusName = caseData.currentStatus?.name || "";
                      const canViewSlides =
                        (isPendingDistribution && !allSlidesReady) ||
                        statusName === "Pending Staining" ||
                        statusName === "Staining" ||
                        statusName === "Awaiting Scan";

                      return (
                        <Table.Tr key={caseData.id}>
                          {/* Select */}
                          <Table.Td>
                            <Checkbox
                              checked={isSelected}
                              disabled={
                                !isPendingDistribution || !allSlidesReady
                              }
                              onChange={(e) => {
                                const newSet = new Set(selectedCases);
                                if (e.currentTarget.checked) {
                                  newSet.add(caseData.id);
                                } else {
                                  newSet.delete(caseData.id);
                                }
                                setSelectedCases(newSet);
                              }}
                            />
                          </Table.Td>

                          {/* Case: Case # + Priority icons + External # + Date */}
                          <Table.Td>
                            <Stack gap={2}>
                              <Group gap={6} wrap="nowrap">
                                <Text
                                  size="sm"
                                  fw={500}
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {getDisplayCaseNumber(caseData)}
                                </Text>
                                {caseData.isRush && (
                                  <Tooltip label="Rush">
                                    <IconUrgent
                                      size={16}
                                      color="red"
                                      style={{ flexShrink: 0 }}
                                    />
                                  </Tooltip>
                                )}
                                {caseData.isHighPriority && (
                                  <Tooltip label="High Priority">
                                    <IconAlertTriangle
                                      size={16}
                                      color="orange"
                                      style={{ flexShrink: 0 }}
                                    />
                                  </Tooltip>
                                )}
                              </Group>
                              {caseData.externalCaseNumber && (
                                <Badge
                                  size="xs"
                                  variant="outline"
                                  color="gray"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  Ext: {caseData.externalCaseNumber}
                                </Badge>
                              )}
                              <Text size="xs" c="dimmed">
                                {formatQueueDate(caseData.dateReceived)}
                              </Text>
                            </Stack>
                          </Table.Td>

                          {/* Patient: Name + DOB + MRN */}
                          <Table.Td>
                            {caseData.patient ? (
                              <Stack gap={0}>
                                <Text size="sm">
                                  {caseData.patient.lastName},{" "}
                                  {caseData.patient.firstName}
                                </Text>
                                {caseData.patient.dateOfBirth && (
                                  <Text size="xs" c="dimmed">
                                    DOB:{" "}
                                    {formatQueueDate(
                                      caseData.patient.dateOfBirth
                                    )}
                                  </Text>
                                )}
                                {caseData.patient.medicalRecordNumber && (
                                  <Text size="xs" c="dimmed">
                                    MRN: {caseData.patient.medicalRecordNumber}
                                  </Text>
                                )}
                              </Stack>
                            ) : (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>

                          {/* Status */}
                          <Table.Td style={{ whiteSpace: "nowrap" }}>
                            <CaseStatusBadge
                              statusName={
                                caseData.currentStatus?.name || "Unknown"
                              }
                              isBlocked={caseData.isBlocked}
                            />
                          </Table.Td>

                          {/* Workflow */}
                          <Table.Td>
                            <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                              {caseData.workflow?.name || "—"}
                            </Text>
                          </Table.Td>

                          {/* Pathologist */}
                          <Table.Td>
                            {caseData.pathologist ? (
                              <Badge
                                size="sm"
                                variant="outline"
                                color={
                                  caseData.pathologist.isRemote
                                    ? "blue"
                                    : "teal"
                                }
                              >
                                {caseData.pathologist.fullName}
                              </Badge>
                            ) : (
                              <Text size="xs" c="dimmed italic">
                                Unassigned
                              </Text>
                            )}
                          </Table.Td>

                          {/* Practice / Center */}
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {caseData.sendingPractice?.name || "—"}
                            </Text>
                            {caseData.surgeryCenterLab?.name && (
                              <Text size="xs" c="dimmed">
                                {caseData.surgeryCenterLab.name}
                              </Text>
                            )}
                          </Table.Td>

                          {/* Physician */}
                          <Table.Td>
                            <Text size="sm">
                              {caseData.physician
                                ? `${caseData.physician.lastName}, ${caseData.physician.firstName}`
                                : "—"}
                            </Text>
                          </Table.Td>

                          {/* Slides Ready */}
                          <Table.Td>
                            <Badge color={allSlidesReady ? "green" : "yellow"}>
                              {readyCount}/{totalCount}
                            </Badge>
                          </Table.Td>

                          {/* Actions */}
                          <Table.Td>
                            <Group gap={8} wrap="nowrap">
                              {caseData.currentStatus?.name ===
                              "Awaiting Scan" ? (
                                <Button
                                  size="xs"
                                  variant="filled"
                                  color="green"
                                  leftSection={<IconCheck size={14} />}
                                  loading={
                                    confirmScanMutation.isPending &&
                                    confirmScanMutation.variables ===
                                      caseData.id
                                  }
                                  onClick={() => handleConfirmScan(caseData.id)}
                                >
                                  Mark as Scanned
                                </Button>
                              ) : isPendingDistribution && allSlidesReady ? (
                                <Button
                                  size="xs"
                                  variant="light"
                                  onClick={() =>
                                    handleConfirmSingle(caseData.id)
                                  }
                                  loading={confirmMutation.isPending}
                                >
                                  Confirm
                                </Button>
                              ) : canViewSlides ? (
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  leftSection={<IconEye size={14} />}
                                  onClick={() => setViewingCaseId(caseData.id)}
                                >
                                  View Slides
                                </Button>
                              ) : (
                                <Text size="xs" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Group>
                          </Table.Td>
                          {queueView === "scanner" && (
                            <Table.Td>
                              <Button
                                size="xs"
                                variant="light"
                                data-case-id={caseData.id}
                                onClick={handleOpenTissue}
                              >
                                Insufficient tissue
                              </Button>
                            </Table.Td>
                          )}
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {tissueCaseId && (
          <SlideInsufficientTissueModal
            caseId={tissueCaseId}
            onClose={handleCloseTissue}
          />
        )}

        {/* Case Slides Review Modal */}
        <Modal.Root
          opened={isModalOpen}
          onClose={closeModal}
          closeOnClickOutside={false}
          size="xl"
          centered
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              {modalData && (
                <Group>
                  <Modal.Title>
                    <Text fw={600}>
                      Case: {getDisplayCaseNumber(modalData.caseInfo)}
                    </Text>
                    {modalData.caseInfo.externalCaseNumber && (
                      <Text size="sm" c="dimmed">
                        {modalData.caseInfo.externalCaseNumber}
                      </Text>
                    )}
                  </Modal.Title>
                  <Badge
                    color={modalData.isCaseReady ? "green" : "yellow"}
                    size="lg"
                  >
                    {modalData.slidesReady}/{modalData.slidesTotal} Slides Ready
                  </Badge>
                </Group>
              )}
              <Modal.CloseButton />
            </Modal.Header>

            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              {modalData && (
                <Stack gap="md">
                  {/* Patient & Client Info */}
                  <Group gap="xl" wrap="wrap">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Patient
                      </Text>
                      <Text size="sm">
                        {modalData.caseInfo.patient
                          ? `${modalData.caseInfo.patient.firstName} ${modalData.caseInfo.patient.lastName}`
                          : "N/A"}
                      </Text>
                    </Stack>
                    {modalData.caseInfo.sendingPractice && (
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Practice
                        </Text>
                        <Text size="sm">
                          {modalData.caseInfo.sendingPractice.name}
                        </Text>
                      </Stack>
                    )}
                    {modalData.caseInfo.physician && (
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Physician
                        </Text>
                        <Text size="sm">
                          {modalData.caseInfo.physician.lastName},{" "}
                          {modalData.caseInfo.physician.firstName}
                        </Text>
                      </Stack>
                    )}
                    {modalData.caseInfo.surgeryCenterLab && (
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Surgery Center
                        </Text>
                        <Text size="sm">
                          {modalData.caseInfo.surgeryCenterLab.name}
                        </Text>
                      </Stack>
                    )}
                  </Group>

                  <Select
                    label="Pathologist"
                    data={pathologistOptions}
                    value={modalData.caseInfo.pathologistId}
                    onChange={handlePathologistSelection}
                    placeholder="Select pathologist"
                    disabled={assignPathologistMutation.isPending}
                  />

                  {/* Slides Table */}
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Part</Table.Th>
                        <Table.Th>Block</Table.Th>
                        <Table.Th>Slide</Table.Th>
                        <Table.Th>Stain</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Ready</Table.Th>
                        <Table.Th>Action</Table.Th>
                        <Table.Th>Insufficient tissue</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {[...modalData.slides]
                        .sort(
                          (
                            a: DistributionSlideInfo,
                            b: DistributionSlideInfo
                          ) => {
                            const partA = (a.partLabel || "").toUpperCase();
                            const partB = (b.partLabel || "").toUpperCase();
                            if (partA !== partB) {
                              return partA.localeCompare(partB);
                            }
                            const blockA = Number(a.blockNumber) || 0;
                            const blockB = Number(b.blockNumber) || 0;
                            if (blockA !== blockB) {
                              return blockA - blockB;
                            }
                            return (
                              (Number(a.slideNumber) || 0) -
                              (Number(b.slideNumber) || 0)
                            );
                          }
                        )
                        .map((slide: DistributionSlideInfo) => (
                          <Table.Tr key={slide.id}>
                            <Table.Td>{slide.partLabel || "-"}</Table.Td>
                            <Table.Td>{slide.blockNumber || "-"}</Table.Td>
                            <Table.Td>{slide.slideNumber}</Table.Td>
                            <Table.Td>
                              <Group gap={4} wrap="nowrap">
                                <Text size="sm">{slide.stainCode || "—"}</Text>
                                {slide.isControlSlide && (
                                  <Badge size="xs" color="blue" variant="light">
                                    Control
                                  </Badge>
                                )}
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="sm" variant="light">
                                {(slide.status || "unknown")
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (c: string) =>
                                    c.toUpperCase()
                                  )}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {slide.requiresConfirmation === false ? (
                                <Badge size="sm" color="gray" variant="light">
                                  Not required
                                </Badge>
                              ) : slide.readyForReview ? (
                                <Badge
                                  size="sm"
                                  color="green"
                                  variant="filled"
                                  leftSection={<IconCheck size={12} />}
                                >
                                  Ready
                                </Badge>
                              ) : (
                                <Badge size="sm" color="red" variant="light">
                                  Not Ready
                                </Badge>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {slide.requiresConfirmation === false ? (
                                <Button
                                  size="compact-xs"
                                  variant="subtle"
                                  disabled
                                >
                                  No confirmation
                                </Button>
                              ) : slide.readyForReview ? (
                                <Button
                                  size="compact-xs"
                                  variant="subtle"
                                  color="red"
                                  onClick={() =>
                                    handleMarkSlideNotReady(slide.id)
                                  }
                                >
                                  Unconfirm
                                </Button>
                              ) : (
                                <Button
                                  size="compact-xs"
                                  variant="light"
                                  color="green"
                                  onClick={() => handleMarkSlideReady(slide.id)}
                                >
                                  Confirm
                                </Button>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <SlideInsufficientTissueCheckbox
                                caseId={modalData.caseInfo.id}
                                slideId={slide.id}
                                name={slide.slideNumber}
                                source={
                                  modalData.caseInfo.currentStatus?.name ===
                                  "Awaiting Scan"
                                    ? "scanner"
                                    : "distribution"
                                }
                              />
                            </Table.Td>
                          </Table.Tr>
                        ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              )}
            </Modal.Body>

            <Divider />
            <Box p="md">
              <Group justify="flex-end">
                <Button variant="default" onClick={closeModal}>
                  Close
                </Button>
                {modalData &&
                  modalData.caseInfo.currentStatus?.name ===
                    "Awaiting Scan" && (
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={() => handleConfirmScan(modalData.caseInfo.id)}
                      loading={confirmScanMutation.isPending}
                    >
                      Mark as Scanned
                    </Button>
                  )}
                {modalData &&
                  modalData.isCaseReady &&
                  modalData.caseInfo.currentStatus?.name ===
                    "Pending Distribution" && (
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={() => handleConfirmSingle(modalData.caseInfo.id)}
                      loading={confirmMutation.isPending}
                    >
                      Confirm Distribution
                    </Button>
                  )}
              </Group>
            </Box>
          </Modal.Content>
        </Modal.Root>

        {/* Filter Modal */}
        <FilterModal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          filters={filters}
          onApply={handleFiltersApply}
          allowedStatusNames={[
            "Pending Staining",
            "Staining",
            "Awaiting Scan",
            "Pending Distribution",
          ]}
        />

        <ControlConfirmationModal
          opened={controlModalOpened}
          onClose={closeControlModal}
        />
      </Box>
    </Container>
  );
}
